import { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";

/**
 * Result modal behind the account statement's synthetic "Live Casino" row
 * (tenexch LiveCasinoStatementModal). That row is the net of every third-party
 * casino bet in the statement's range (they get no row of their own), so there
 * is no single round to show: choose a casino type and it lists that provider's
 * transactions for the same range (`fromDate` / `toDate`). The dropdown is the
 * live site's provider list (server). Nothing is listed until one is picked.
 *
 * `load(params)`: the page's fetcher — player POST /user/account-statement-v2/live-casino,
 * d99-admin POST /admin/reports/statementv2/live-casino (for the typed client).
 * Markup/classes are d99's (Live Casino Bets report table).
 */

function pad(n) { return String(n).padStart(2, "0"); }
function fmtDateTime(d) {
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d ?? "");
  return `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)}/${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;
}
function fmtNum(v) {
  const n = Number(v);
  if (isNaN(n)) return v;
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

export default function LiveCasinoStatementModal({ show, onHide, fromDate, toDate, load }) {
  const [casinoType, setCasinoType] = useState("");
  const [providers, setProviders] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Every open starts clean — no provider, no rows from the last visit.
  useEffect(() => {
    if (!show) return;
    setCasinoType("");
    setRows([]);
    setLoading(false);
  }, [show]);

  // The server's provider list fills the dropdown ([{ value, label }]).
  useEffect(() => {
    if (!show) return undefined;
    let alive = true;
    load({ fromDate, toDate })
      .then((res) => { if (alive) setProviders(res?.success ? res.providers || [] : []); })
      .catch(() => { if (alive) setProviders([]); });
    return () => { alive = false; };
  }, [show, fromDate, toDate, load]);

  // Picking a provider triggers the call — there is no Submit here.
  useEffect(() => {
    if (!show || !casinoType) return undefined;
    let alive = true;
    setLoading(true);
    load({ fromDate, toDate, provider: casinoType })
      .then((res) => { if (alive) setRows(res?.success ? res.data || [] : []); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [show, casinoType, fromDate, toDate, load]);

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Result</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row row5">
          <div className="col-lg-3 col-md-4">
            <div className="mb-2 input-group position-relative">
              <select
                className="form-select"
                name="type"
                value={casinoType}
                onChange={(e) => setCasinoType(e.target.value)}
              >
                <option value="">Select Casino Type</option>
                {providers.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="mt-2 table-responsive">
          <table role="table" className="table table-bordered table-striped">
            <thead>
              <tr role="row">
                <th colSpan="1" role="columnheader" className="game-name">Game Name</th>
                <th colSpan="1" role="columnheader" className="report-type">Type</th>
                <th colSpan="1" role="columnheader" className="report-amount text-end">Amount</th>
                <th colSpan="1" role="columnheader" className="report-amount text-end">Total</th>
                <th colSpan="1" role="columnheader" className="report-date">Date</th>
                <th colSpan="1" role="columnheader">Round Id</th>
                <th colSpan="1" role="columnheader">Transaction Id</th>
              </tr>
            </thead>
            <tbody role="rowgroup">
              {loading ? (
                <tr role="row"><td colSpan="7" className="text-center"><i className="fa fa-spinner fa-spin"></i></td></tr>
              ) : rows.length === 0 ? (
                <tr role="row"><td role="cell" colSpan="7" className="text-center text-muted">No records found</td></tr>
              ) : rows.map((item, i) => (
                <tr role="row" key={i}>
                  <td role="cell" className="game-name">{item.gameName || "-"}</td>
                  <td role="cell" className="report-type">{item.type || "-"}</td>
                  <td role="cell" className="report-amount text-end">{fmtNum(item.amount || 0)}</td>
                  <td role="cell" className="report-amount text-end">{fmtNum(item.total || 0)}</td>
                  <td role="cell" className="report-date">{item.date ? fmtDateTime(item.date) : "-"}</td>
                  <td role="cell">{item.roundId || "-"}</td>
                  <td role="cell">{item.transactionId || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal.Body>
    </Modal>
  );
}
